import { type OrganizationManager } from "@entities";
import {
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { organizationManagerRepo } from "@repositories";
import { useEffect, useMemo, useState, type FC } from "react";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useRequest } from "../../contexts/RequestProvider";
import { useUser } from "../../contexts/UserProvider";
import { CheckboxIcon } from "@shared/frontend/icons";

export const ManagerSelect: FC = () => {
  const [managers, setManagers] = useState<OrganizationManager[]>([]);
  const { currentUser } = useUser();
  const { currOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);

  const { currentEvent, setCurrentEvent } = useRequest();

  const selected = useMemo(() => {
    if (currentEvent.manager) return currentEvent.manager;
    if (currentEvent.managerId) {
      const fromList = managers.find((m) => m.id === currentEvent.managerId);
      if (fromList) return fromList;
    }
    return currentUser;
  }, [
    currentEvent.manager,
    currentEvent.managerId,
    managers,
    currentUser,
  ]);

  // Default manager for new requests only - never overwrite an existing managerId.
  useEffect(() => {
    if (loading || !currentUser) return;
    if (currentEvent.managerId || currentEvent.id) return;
    const defaultManager = managers.find((m) => m.id === currentUser.id);
    setCurrentEvent((prev) => ({
      ...prev,
      managerId: currentUser.id,
      manager: prev.manager ?? defaultManager,
    }));
  }, [
    loading,
    currentUser?.id,
    currentEvent.managerId,
    currentEvent.id,
    managers,
  ]);

  useEffect(() => {
    getManagers();
  }, [currentUser, currOrganization]);

  const getManagers = async () => {
    if (!currentUser || !currOrganization) return;
    const organizationManagers = await organizationManagerRepo.find({
      where: { organization: currOrganization },
      orderBy: { name: "asc" },
      limit: 300,
    });
    setManagers(
      organizationManagers.sort((a, b) =>
        a.id === currentUser.id ? -1 : b.id === currentUser.id ? 1 : 0,
      ),
    );
    setLoading(false);
  };

  if (!currentUser || !currOrganization) return;
  return (
    <Stack className="field-container managers-container">
      <label className="field-label">מנהל</label>
      <Select
        slotProps={{
          root: { className: "field" },
        }}
        MenuProps={{
          disableScrollLock: true,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- mui naming
          PaperProps: {
            sx: {
              borderRadius: "20px",
              width: "14vw",
            },
          },
        }}
        value={!loading ? selected?.id : ""}
        onChange={(event) => {
          const newValue = event.target.value;
          const manager = managers?.find((m) => m.id === newValue);
          setCurrentEvent((prev) => ({
            ...prev,
            manager,
            managerId: manager?.id ?? "",
          }));
        }}
        renderValue={(selected) => {
          const manager = managers.find((m) => m.id === selected);
          return loading ? (
            <div>
              <CircularProgress size={16} />
            </div>
          ) : (
            <div>
              <Chip label={manager?.name || ""} />
            </div>
          );
        }}
      >
        {managers.map((manager) => (
          <MenuItem key={manager.id} value={manager.id}>
            <Checkbox
              checkedIcon={<CheckboxIcon checked />}
              icon={<CheckboxIcon />}
              checked={selected?.id === manager.id}
            />
            <ListItemText primary={manager.name} />
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};
